import React, { useEffect, useReducer, useRef, useState } from "react";

import { Input, InputChangeEvent, SwitchChangeEvent } from "@progress/kendo-react-inputs";
import { getInitialState } from "@selas/state-management";
import { EntityEditor, handleChange, IAddEntityScreenProps, IEditEntityScreenProps, YesNoSwitch } from "@selas/ui-components";
import { isNullOrEmpty } from "@selas/utils";
import { useTranslation } from "react-i18next";

import Endpoint from "../../../services/api/endpoint";
import { serviceTypeReducer } from "../../../state/reducers";
import { IServiceType } from "../../../utils/types/models";

const ServiceTypeEditor: React.FC<IAddEntityScreenProps<IServiceType> | IEditEntityScreenProps<IServiceType>> = (props: IAddEntityScreenProps<IServiceType> | IEditEntityScreenProps<IServiceType>) => {
	const { t } = useTranslation();

	const [state, dispatch] = useReducer(serviceTypeReducer, getInitialState<IServiceType>());
	const initialServiceType: IServiceType = {
		id: (props as IEditEntityScreenProps<IServiceType>).recordId || 0,
		name: "",
		copyServices: false,
		active: true
	};
	const [serviceType, setServiceType] = useState<IServiceType>(initialServiceType);
	const [dataChanged, setDataChanged] = useState<boolean>(false);
	const firstField: React.MutableRefObject<Input> = useRef();

	useEffect(() => {
		if (state.entity) {
			setServiceType(state.entity);
		}
	}, [state.entity]);

	function onChange(event: InputChangeEvent | SwitchChangeEvent): void {
		setServiceType(handleChange(serviceType, event));
		setDataChanged(true);
	}

	function getErrorMessages(): string[] {
		const messages: string[] = [];
		if (isNullOrEmpty(serviceType.name)) {
			messages.push(t("fill_in_required_field", { field: t("name").toLowerCase() }));
		}
		return messages;
	}

	function onActionButtonClicked(close: boolean, record: IServiceType): void {
		if (record && !close) {
			setServiceType(record);
			setDataChanged(false);
		}
		props.actionButtonClicked(close, record);
	}

	const readonly: boolean = (props as IEditEntityScreenProps<IServiceType>).readonly;

	return (
		<EntityEditor
			width="70%"
			record={serviceType}
			endpoint={Endpoint.ServiceTypes}
			entityState={state}
			entityType={t("serviceType")}
			dispatch={dispatch}
			dataChanged={dataChanged}
			readonly={readonly}
			recordName={serviceType.name}
			actionButtonClicked={onActionButtonClicked}
			getErrorMessages={getErrorMessages}
			firstFieldRef={firstField}
		>
			<div className="k-form">
				<label className="k-form-field">
					<span>{t("name")} *</span>
					<Input name="name" ref={firstField} className="full-width-field" value={serviceType.name} onChange={onChange} required disabled={readonly} />
				</label>
				<div className="k-form-field">
					<div>{t("copyServices")}</div>
					<YesNoSwitch name="copyServices" checked={serviceType.copyServices} onChange={onChange} disabled={readonly} />
				</div>
				{!props.hideActive && (
					<div className="k-form-field">
						<div>{t("active")}</div>
						<YesNoSwitch name="active" checked={serviceType.active} onChange={onChange} disabled={readonly} />
					</div>
				)}
			</div>
		</EntityEditor>
	);
};

export default ServiceTypeEditor;
